/**
 * Tenant (white label) registry.
 *
 * The customer web is served as ONE deployment for every tenant - the domain it
 * is reached on decides which theme to use. Adding a tenant is:
 *
 *  1. add an entry below (colors + logo + the domains it answers on),
 *  2. drop the assets in `public/tenants/<key>/`,
 *  3. point the domain at the oo-customer-web service (gitops ingress).
 *
 * Kept deliberately small - only what actually differs between tenants today.
 */
/**
 * Plain JavaScript on purpose: `server.js` imports this module to theme
 * index.html per request, and Node cannot import TypeScript. The types live
 * next to it in `tenants.d.ts`.
 *
 * @typedef {import("./tenants").TenantKey} TenantKey
 * @typedef {import("./tenants").TenantTheme} TenantTheme
 */

/** @type {Record<TenantKey, TenantTheme>} */
const tenants = {
	bilda: {
		key: "bilda",
		name: "BILDA Personal",
		domains: ["kundportal.bildapersonal.se", "bilda.origoworks.se"],
		colors: {
			primary: "#3F7EAB",
			accent: "#4E7EA7",
			secondary: "#2B2B2B",
			background: "#FFFFFF",
			onPrimary: "#FFFFFF",
		},
		logo: "/tenants/bilda/logo.svg",
		logoSmall: "/tenants/bilda/logo-small.png",
		loginBackgroundImage: "/tenants/bilda/login-bg.jpg",
	},
	origo: {
		key: "origo",
		name: "Origo Works",
		domains: [
			"kund.origoworks.se",
			"oo-customer-web.c2.frost.se",
			"oo-customer-web.test-1.fruster.se",
		],
		colors: {
			primary: "#4BB769",
			accent: "#4BB769",
			secondary: "#1D1D1D",
			background: "#FFFFFF",
			onPrimary: "#FFFFFF",
		},
		logo: "/tenants/origo/logo.png",
	},
	bemannia: {
		// TODO: domain not routed yet - add it to the gitops ingress when it is.
		key: "bemannia",
		name: "Bemannia GO",
		domains: ["kund.bemannia.se", "bemannia.origoworks.se"],
		colors: {
			primary: "#002C85",
			accent: "#002C85",
			secondary: "#212121",
			background: "#F9F9F9",
			onPrimary: "#FFFFFF",
		},
		logo: "/tenants/bemannia/logo.png",
	},
	meshia: {
		// TODO: domain not routed yet - add it to the gitops ingress when it is.
		key: "meshia",
		name: "Meshia",
		domains: ["kund.meshia.se", "meshia.origoworks.se"],
		colors: {
			primary: "#FF6735",
			accent: "#FF6735",
			secondary: "#473D3A",
			background: "#FFFFFF",
			onPrimary: "#FFFFFF",
		},
		logo: "/tenants/meshia/logo.png",
	},
};

/** Used for hosts that are not mapped to a tenant, such as localhost. */
export const DEFAULT_TENANT = "bilda";

export function isTenantKey(value) {
	return !!value && value in tenants;
}

export function getTenant(key) {
	return tenants[key];
}

export function getTenantByDomain(hostname) {
	const host = hostname.toLowerCase().split(":")[0];

	return Object.values(tenants).find((tenant) =>
		tenant.domains.some((domain) => host === domain || host.endsWith(`.${domain}`)),
	);
}

export default tenants;
